import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { TableTopPanel } from 'components/TableTopPanel';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { WorkOrderSeasonData } from 'gql/graphql';
import { Loader } from 'uikit';

import type { AgGridReact } from 'ag-grid-react';
import { useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { gqlUerWorkOrderTotals } from '../workOrderQueries';

import { useOrderSummaryTableColumns } from './hooks';

import Style from './WorkOrderSummaryTable.module.css';

interface IWorkOrderSummaryTable {
    costRounding: number;
    workOrderSeasons: WorkOrderSeasonData[];
}

export const WorkOrderSummaryTable = (props: IWorkOrderSummaryTable) => {
    const { costRounding, workOrderSeasons } = props;
    const { orderId: workOrderId } = useParams();

    const tableRef = useRef<AgGridReact | null>(null);
    const tableApi = useMemo(() => (tableRef && 'current' in tableRef ? tableRef.current : undefined), [tableRef]);

    const variables = useMemo(() => ({ workOrderId: Number(workOrderId) }), [workOrderId]);

    const [workOrderResultData, workOrderResultStatus] = useRequest({
        requestId: 'uerWorkOrderTotals',
        query: gqlUerWorkOrderTotals,
        variables,
        withEffect: Boolean(workOrderId),
    });

    const workOrderResult = workOrderResultData?.uerWorkOrderTotals?.data;
    const isLoading = workOrderResultStatus === RequestStatus.FETCHING;

    const { fullSetSettingsColumns, isColumnVisible, columns, tableSettingsConfirm } = useOrderSummaryTableColumns({
        instanceId: workOrderId,
        costRounding,
        workOrderSeasons,
    });

    const totalItems = useMemo(
        () => workOrderResultData?.uerWorkOrderTotals?.data?.length || 0,
        [workOrderResultData?.uerWorkOrderTotals?.data?.length],
    );

    return (
        <div className={Style.workOrderSummaryTable}>
            <TableTopPanel
                totalItems={totalItems}
                fetchedItems={totalItems}
                optionalColumns={fullSetSettingsColumns}
                isColumnVisible={isColumnVisible}
                handleTableSettingsConfirm={tableSettingsConfirm}
                tableApi={tableApi}
            />
            {isLoading && <Loader />}
            {!isLoading && (
                <div className="workOrderPrice">
                    <AgGridTable
                        ref={tableRef}
                        columnDefs={columns as any}
                        rowData={workOrderResult}
                        defaultColDef={{
                            sortable: false,
                        }}
                    />
                </div>
            )}
        </div>
    );
};

WorkOrderSummaryTable.displayName = 'WorkOrderSummaryTable';
